import React, { useContext, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FuelCallout } from '@frete.com/fuel-react';
import { FuelFlex } from '@frete.com/fuel-station-react';
import theme from '@frete.com/fuel-foundation/fretebras/theme.json';
import { WaypointsUpdatePayload } from '@interface/journeys';
import AppContext from '@context';
import { updateWaypoints } from '@services/journeys';
import { EditAddressPropsInterface } from './EditAddressTypes';
import Form from './Form';
import editAddressSchema, { EditAddressType } from './Form/schema';

function EditAddress({
  isShow,
  journeyId,
  renderLocation,
  onCancel,
  onSucess,
}: EditAddressPropsInterface) {
  const [isLoading, setIsLoading] = useState(false);
  const { dispatch } = useContext(AppContext);
  const editAddressJourneyHook = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(editAddressSchema),
  });

  function handleFormatPayload(data: EditAddressType): WaypointsUpdatePayload {
    return {
      waypoints: [
        {
          kind: 'pickup',
          order: 1,
          place: {
            zipCode: data.pickup.place.zipCode || '',
            ...data.pickup.place,
          },
        },
        {
          kind: 'delivery',
          order: 2,
          place: {
            zipCode: data.delivery.place.zipCode || '',
            ...data.delivery.place,
          },
        },
      ],
    };
  }

  function handleSubmit(data: EditAddressType) {
    const dataFormatted = handleFormatPayload(data);

    if (!journeyId || isLoading) {
      return;
    }

    setIsLoading(true);

    updateWaypoints(journeyId, dataFormatted)
      .then(() => {
        window.dispatchEvent(
          new CustomEvent('notificationShow', {
            detail: {
              isShow: true,
              heading: 'Dados da rota salvos com sucesso!',
              severity: 'success',
            },
          }),
        );
        dispatch({
          type: 'journey_update_address',
          payload: {
            journeyId,
            pickup: dataFormatted.waypoints[0],
            delivery: dataFormatted.waypoints[1],
          },
        });

        if (onSucess) {
          onSucess();
        }
      })
      .catch(() => {
        window.dispatchEvent(
          new CustomEvent('notificationShow', {
            detail: {
              isShow: true,
              heading: 'Não foi possível realizar esta ação',
              message: 'Tente novamente mais tarde',
              severity: 'error',
            },
          }),
        );
      })
      .finally(() => {
        setTimeout(() => {
          setIsLoading(false);
        });
      });
  }

  if (!isShow || !journeyId) {
    return null;
  }

  return (
    <>
      <FuelFlex
        defaultProps={{
          customMargin: `${theme.spacing.km6} 0`,
        }}
      >
        <FuelCallout
          message="Complete o endereço para visualizar as estimativas da viagem com locais de coleta e entrega exatos."
          icon="distance"
          fullWidth
        />
      </FuelFlex>
      <FuelFlex
        defaultProps={{
          flexDirection: 'column',
          gap: 'km10',
        }}
      >
        <FormProvider {...editAddressJourneyHook}>
          <Form
            isLoading={isLoading}
            renderLocation={renderLocation}
            onConfirm={(data) => handleSubmit(data)}
            onCancel={onCancel}
          />
        </FormProvider>
      </FuelFlex>
    </>
  );
}

export default EditAddress;
